import {Injectable} from '@angular/core';
import {BasePlatformHandler} from '@shared/base/base-platform-handler.service';
import {Observable, Subject} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {CommandService} from '@core/services/command.service';
import {ActivatedRoute, Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {Store} from '@ngrx/store';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {SnackBarService} from '@core/services/snack-bar.service';
import {FileManagementService} from '@core/services/file-management.service';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {DataTracingService} from '@features/data-tracing/services/data-tracing.service';
import {ParticleCacheService} from '@core/services/particle-cache.service';
import {FileStoreCommonService} from '@features/file-manager/services/file-store-common.service';
import {ItpDesktopUtilityService} from '@core/services/itpdesktopservices/itp-desktop-utility.service';
import {FileManagerService} from '@features/file-manager/services/file-manager.service';
import {ShareItemService} from '@shared/services/share-item.service';
import {VirtualGridSelectionService} from '@core/services/virtual-grid-selection.service';
import {FlowChartDataService} from '@features/data-survey/services/flow-chart-data.service';
import {ParticleService} from '@core/services/particle.service';
import {SmartGridService} from '@core/services/smart-grid.service';
import {ParticleState} from '@shared/store/reducers/particle.reduer';
import {TaskManagerService} from '@features/task-manager/services/task-manager.service';
import {ItrcePlannerServices} from '@features/itrce-planner/services/itrce-planner.services';
import {PlatformCommand} from '@shared/commands/command-base';
import {DialogModel} from '@shared/models/dialog.model';
import {
  AddFxRateReportDynamicFieldsCommand,
  ChangeSummaryHeaderCommand,
  CreateReportCommand,
  DeleteReportCommand,
  ExportReportCommand
} from '@features/fx-rate/commands/fx-rate-report.command';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';
import {
  SetActivityFeedAction,
} from "@shared/components/platform-sidebar/store/platform-sidebar.action";
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {FieldSection} from '@shared/models/field-section';
import {FxRateReportModel} from '@features/fx-rate/models/fx-rate-report.model';
import {GetFxRateReportAction} from '@features/fx-rate/store/actions/fx-rate.action';
import {getReport} from '@features/fx-rate/store/selectors/fx-rate.selectors';
import {takeUntil} from 'rxjs/operators';
import {Configuration} from '@shared/models/configuration';
import {GetFieldSectionsAction} from '@shared/store/actions/virtual-grid.actions';
import {FxRateRateType} from '@features/fx-rate/constants/fx-rate-constants';
import {TimeZoneService} from '@core/services/time-zone.service';
import {FxRateState} from '@features/fx-rate/store/reducers/fx-rate.reducer';
import {IVirtualGridCollectionState} from '@shared/store/reducers/virtual-grid.reducer';
import {isNotNullOrEmptyString, isNullOrEmptyString} from '@progress/kendo-data-query/dist/npm/utils';

@Injectable({
  providedIn: 'root',
})

export class PlatformToolFxRatesHandler extends BasePlatformHandler {
  reportId: string;
  reportTitle: string;
  params: any;

  showNote: boolean = false;
  invalidateSubject: InvalidateSubject;
  sourceType = 'FxRateReportDetails';
  newSourceType: string;
  fieldSections$: Observable<FieldSection[]>;
  reportingCurrencies: string;
  translationCurrencies: string;
  reportModel: FxRateReportModel = null;
  hasSummaryData: boolean = false;
  hasGeneralData: boolean = false;
  reportModel$: Observable<FxRateReportModel>;
  unsubscribe$ = new Subject<void>();

  constructor(
    public httpClient: HttpClient,
    public commandService: CommandService,
    public router: Router,
    public dialogService: DialogService,
    public fuseSplashScreenService: FuseSplashScreenService,
    public queryState: Store<IQueryState>,
    public snackBarService: SnackBarService,
    public fileManagementService: FileManagementService,
    public platformQueryService: PlatformQueryService,
    public dataTracingService: DataTracingService,
    public particleCacheService: ParticleCacheService,
    public fileStoreCommonService: FileStoreCommonService,
    public itpDesktopUtilityService: ItpDesktopUtilityService,
    public fileManagerService: FileManagerService,
    public shareItemService: ShareItemService,
    public virtualGridSelectionService: VirtualGridSelectionService,
    public flowChartDataService: FlowChartDataService,
    private platformSidebarStore: Store<PlatformSidebarState>,
    private timeZoneService: TimeZoneService,
    private fxratestore: Store<FxRateState>,
    private store: Store<IVirtualGridCollectionState>,
  ) {
    super(
      httpClient,
      commandService,
      router,
      dialogService,
      fuseSplashScreenService,
      queryState,
      snackBarService,
      fileManagementService,
      platformQueryService,
      dataTracingService,
      particleCacheService,
      fileStoreCommonService,
      itpDesktopUtilityService,
      fileManagerService,
      shareItemService,
      virtualGridSelectionService,
      flowChartDataService
    );
  }

  executeCommand(command: PlatformCommand, callBack?: any) {
    if (command.fieldValues['reportId']) {
      this.reportId = command.fieldValues['reportId'];
    }

    if (command.name === 'AddReport') {
      this.createReport();
      return;
    } else if (command.name === 'EditReport') {
      this.reportSettings();
      return;
    } else if (command.name === 'ExportReport') {
      this.exportReport();
      return;
    }
    super.executeCommand(command, callBack);
  }

  invalidate(rateView: string) {
    const config = Configuration.getSmartGridConfig(this.sourceType);
    config.SourceType = this.newSourceType;
    config.Parameters['REPORT_ID'] = this.reportId;
    config.Parameters['RATE_VIEW'] = rateView;
    Configuration.setSmartGridConfig(config.SourceType, config);
    this.store.dispatch(new GetFieldSectionsAction(this.newSourceType));
    this.invalidateSubject.invalidate();
  }

  valueChanged(event) {
    const reportName = event?.item?.cells?.ReportName?.value?.trim();
    if (!reportName) {
      console.warn('Report name is missing');
      return;
    }
    const reportId = event?.item?.id;
    if (!reportId) {
      console.warn('Report id is missing');
      return;
    }
    if (!event?.action && isNotNullOrEmptyString(reportName) && event?.fieldDefinition?.key === 'ReportName') {
      this.onGridCellValueChange(event);
      return;
    }
    const actionId = event.action.id;
    switch (actionId) {
      case 'Edit':
        this.router.navigateByUrl(`itrce/fxrates/add-report/${event.item.id}`);
        break;

      case 'Delete':
        this.deleteReport(event.item.id);
        break;

      case 'ShowActivities':
        this.platformSidebarStore.dispatch(
          new SetActivityFeedAction({
            type: 'ReferenceCode',
            id: `${event.item.id}`,
            title: reportName,
            isDefault: false,
            isUserAction: true
          })
        );
        break;

      default:
        console.warn('Unknown action:', actionId);
    }
  }

  onGridCellValueChange(event) {
    if (event.fieldDefinition && event.fieldDefinition.key === 'ReportName') {
      const item = event.item;
      const reportName = item.cells['ReportName'].value;

      const reportingCurrencies = item.cells['ReportingCurrencies'];
      const translationCurrencies = item.cells['TranslationCurrencies'];

      if (reportingCurrencies && reportingCurrencies.value && translationCurrencies && translationCurrencies.value) {
        this.addDynamicFieldsCommand(event.item.id, reportName);
      } else {
        this.dialogService.showDialogMessage('Message', 'Please edit the report and select reporting currencies and translation currencies first.');
      }

      // this.router.navigateByUrl(`itrce/fxrates/fx-rates-view-report/${reportName}/${event.item.id}`);
      return;
    }
    if (event.action) {
      switch (event.action.id) {
        case 'Edit':
          this.router.navigateByUrl(`itrce/fxrates/add-report/${event.item.id}`);
          break;
        case 'Delete':
          this.deleteReport(event.item.id);
          break;
        case 'ShowActivities':
          this.platformSidebarStore.dispatch(new SetActivityFeedAction({type: 'ReferenceCode', 'id': `${event.item.id}`, 'title': event.item.cells['ReportName'].value, isDefault: false, isUserAction: true}));
          break;
      }
    }
  }

  addDynamicFieldsCommand(reportId: string, reportName: string) {
    const command = new AddFxRateReportDynamicFieldsCommand();
    command.reportId = reportId;
    command.sourceType = 'FxRateReportDetails';
    command.rateView = 'SimpleAverage';
    this.commandService.executeCommand(command).pipe().subscribe(state => {
      if (state) {
        console.log(state);
        this.invalidateSubject.invalidate();
        const config = Configuration.getSmartGridConfig(this.sourceType);
        config.SourceType = state.Payload.SourceType;
        config.Parameters['REPORT_ID'] = reportId;
        Configuration.setSmartGridConfig(config.SourceType, config);
        // Segment-array navigation so "(" and ")" in the report name are escaped (see FxRateReportContentComponent).
        this.router.navigate(['itrce', 'fxrates', 'fx-rates-view-report', reportName, reportId]);
      }
    });
  }

  deleteReport(reportId: string) { // on html empty string passed for build fix.
    const command = new DeleteReportCommand();
    command.reportId = reportId;
    this.commandService.executeCommand(command).pipe().subscribe(state => {
      if (state) {
        this.invalidateSubject.invalidate();
        this.snackBarService.showInformation('Deleted Successfully');
      }
    });
  }

  exportReport() {
    this.fuseSplashScreenService.show();
    const command = new ExportReportCommand();
    command.reportId = this.reportId;
    this.commandService.executeCommand(command).pipe().subscribe(state => {
      if (state) {
        console.log(state);
        this.fileManagementService.downloadClientFileWithId(state.Payload).pipe().subscribe((payload: any) => {
          this.fuseSplashScreenService.hide();
          this.fileManagementService.openSaveFileWindow(payload, 'FxReport.xlsx');
        }, (error) => {
          this.fuseSplashScreenService.hide();
        });
      }
    }, (error) => {
      this.fuseSplashScreenService.hide();
    });
  }

  reportSettings() {
    this.router.navigateByUrl(`itrce/fxrates/add-report/${this.reportId}`);
  }

  createReport() {
    const dialogModel = DialogModel.create('Create Report', 'CreateFxRateDialogComponent', {});
    this.dialogService.showOkCancelDialog(dialogModel).subscribe(res => {
      if (res.action === 'OK') {
        if (res.data.input) {
          const command = new CreateReportCommand();
          command.reportName = res.data.input['reportName'];
          command.rateType = res.data.input['rateType'];
          this.fuseSplashScreenService.show();
          this.commandService.executeCommand(command).subscribe(data => {
            this.fuseSplashScreenService.hide();
            if (data) {
              const reportId = data.Payload.Id;
              this.router.navigateByUrl(`itrce/fxrates/add-report/${reportId}`);
            }
          }, () => {
            this.fuseSplashScreenService.hide();
          });
        }
      }
    });
  }

  closeNote() {
    this.showNote = false;
  }

  showNoteOrNot() {
    if (this.reportModel.detailRateView === 'Month-End') {
      this.showNote = false;
      return;
    }
    if (this.reportModel.rateType === FxRateRateType.TaxYearRate) {
      this.showNote = true;
      return;
    }
    if (this.reportModel.rateType === FxRateRateType.PeriodRate) {
      const startDate = new Date(this.reportModel.dates[0]);
      const endDate = new Date(this.reportModel.dates[1]);
      const dateLimit = new Date((startDate.getTime() + (1000 * 60 * 60 * 24 * 90)));
      if (endDate > dateLimit) {
        this.showNote = true;
      }
    }
  }

  goToEditPage() {
    this.router.navigateByUrl(`itrce/fxrates/add-report/${this.reportId}`);
  }

  rateViewChange(rateView: string) {
    const command = new ChangeSummaryHeaderCommand();
    command.sourceType = this.newSourceType;
    command.rateView = rateView;
    this.commandService.executeCommand(command).pipe().subscribe(state => {
      if (state) {
        this.invalidate(rateView);
      }
    });

  }

  private fixDates(reportModel: FxRateReportModel) {
    if (!reportModel.isDateFixed) {
      for (let i = 0; i < reportModel.dates.length; i++) {
        reportModel.dates[i] = this.timeZoneService.addTimezoneOffsetToUTCDate(reportModel.dates[i]);
      }
      reportModel.isDateFixed = true;
    }
  }
}
